import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes, FaWhatsapp, FaTrashAlt, FaPrint } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const WHATSAPP_NUMARA = "905377981129";

const CartDrawer = ({ acik, kapat }) => {
  const { t } = useTranslation();
  const { satirlar, bos, adetDegistir, cikar, temizle } = useCart();

  // ESC ile kapat + arka planı kilitle
  useEffect(() => {
    if (!acik) return;
    const tus = (e) => e.key === "Escape" && kapat();
    window.addEventListener("keydown", tus);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", tus);
      document.body.style.overflow = "";
    };
  }, [acik, kapat]);

  const whatsappBaglantisi = () => {
    const satirMetni = satirlar
      .map((s) => `• ${s.adet} x ${s.ad} (${s.boyut})`)
      .join("\n");

    const mesaj = [
      t("shop.whatsappIntro"),
      "",
      satirMetni,
      "",
      t("shop.whatsappOutro"),
    ].join("\n");

    return `https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(mesaj)}`;
  };

  return (
    <AnimatePresence>
      {acik && (
        <>
          {/* Karartma */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={kapat}
            className="fixed inset-0 z-[110] bg-ink/50 backdrop-blur-sm"
          />

          {/* Çekmece */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
            role="dialog"
            aria-label={t("shop.cartTitle")}
            className="fixed inset-y-0 right-0 z-[120] flex w-full max-w-md flex-col bg-white shadow-pop"
          >
            {/* Başlık */}
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-[16px] font-semibold text-ink">
                {t("shop.cartTitle")}
              </h2>
              <button
                onClick={kapat}
                aria-label={t("ui.close")}
                className="flex h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-canvas hover:text-ink"
              >
                <FaTimes />
              </button>
            </div>

            {/* İçerik */}
            {bos ? (
              <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
                <p className="text-[15px] font-medium text-ink">
                  {t("shop.cartEmpty")}
                </p>
                <p className="mt-2 text-[14px] leading-relaxed text-muted">
                  {t("shop.cartEmptyHint")}
                </p>
              </div>
            ) : (
              <ul className="flex-1 divide-y divide-line overflow-y-auto">
                {satirlar.map((s) => (
                  <li key={s.id} className="flex gap-4 p-5">
                    <div className="media h-16 w-16 shrink-0 rounded-md border border-line bg-white">
                      <img
                        src={s.gorsel}
                        alt={s.ad}
                        className="h-full w-full object-contain p-1.5"
                      />
                    </div>

                    <div className="min-w-0 flex-1 text-start">
                      <p className="eyebrow">{s.marka}</p>
                      <p className="mt-1 text-[14px] font-medium leading-snug text-ink">
                        {s.ad}
                      </p>
                      <p className="mt-0.5 text-[13px] text-muted">{s.boyut}</p>

                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex items-center rounded-md border border-line">
                          <button
                            onClick={() => adetDegistir(s.id, -1)}
                            aria-label="-"
                            className="flex h-8 w-8 items-center justify-center text-muted transition-colors hover:text-ink"
                          >
                            −
                          </button>
                          <span className="w-7 text-center text-[14px] font-medium tabular-nums text-ink">
                            {s.adet}
                          </span>
                          <button
                            onClick={() => adetDegistir(s.id, 1)}
                            aria-label="+"
                            className="flex h-8 w-8 items-center justify-center text-muted transition-colors hover:text-ink"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => cikar(s.id)}
                          className="text-[13px] text-muted transition-colors hover:text-accent"
                        >
                          {t("shop.remove")}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Alt aksiyonlar */}
            {!bos && (
              <div className="border-t border-line p-5">
                <a
                  href={whatsappBaglantisi()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#1FB955]"
                >
                  <FaWhatsapp size={18} />
                  {t("shop.whatsappSend")}
                </a>

                {/* Toptan / şantiye: sepet, firma ve teslimat bilgisiyle birlikte teklif formuna aktarılır */}
                <Link
                  to="/toptan-teklif"
                  onClick={kapat}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-line px-5 py-3 text-[13.5px] font-semibold text-ink transition-colors hover:border-ink"
                >
                  {t("quote.fromCartCta")}
                </Link>

                {/* Fiyat teklifi görünümünde yazdır / PDF */}
                <Link
                  to="/teklif-listesi"
                  onClick={kapat}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-line px-5 py-3 text-[13.5px] font-semibold text-ink transition-colors hover:border-ink"
                >
                  <FaPrint size={13} aria-hidden="true" />
                  {t("printList.cartCta")}
                </Link>

                <button
                  onClick={temizle}
                  className="mt-3 flex w-full items-center justify-center gap-2 py-2 text-[13px] text-muted transition-colors hover:text-accent"
                >
                  <FaTrashAlt size={12} />
                  {t("shop.clear")}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
